'use client';

import { Button, IosClose } from '@sgcmpteltd/uikit-react';
import {
  BUTTON_COLOR_GRAY,
  BUTTON_COLOR_RED,
  BUTTON_VARIANT_OUTLINED,
} from '@sgcmpteltd/uikit-react/components/button';
import cx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getCarDetailData } from '@/actions/new-car/car-detail';
import {
  setNewcarItemsCookies,
} from '@/actions/new-car/set-newcar-items-cookies';
import type { CamelCaseCarDetail } from '@/apis/server/new-car/v1/car-detail';
import type {
  CamelCaseAdAlert,
  CamelCaseAdAlertList,
  CamelCaseCarAlert,
  CamelCaseCarAlertList,
  CamelCaseResponseData as CamelCaseMyAlert,
} from '@/apis/server/new-car/v1/my-alert';
import { isCamelCaseCarAlert } from '@/apis/server/new-car/v1/my-alert/utils';
import type {
  AlertList,
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card';
import {
  TITLE_HEADING_DIV,
} from '@/components/section-container/base/constants';
import { SectionContainer } from '@/components/section-container/page';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';

import { CarItem } from './car-item/component';
import { CompareModal } from './compare-modal';
import { FeaturesTable } from './features-table';
import { OverviewTable } from './overview-table';
import { SpecsTable } from './specs-table';
import styles from './styles.module.scss';
import type { Component, NavOption } from './types';

export const PageContent: Component = ({
  brandList,
  compareList: compareCarDetailList,
  promotions,
  myAlerts = null,
}) => {
  const [
    myAlertList,
    setMyAlertList,
  ] = useState<CamelCaseMyAlert | null>(myAlerts);
  const [
    compareList,
    setCompareItems,
  ] = useState(() => {
    const newArray: (
      CamelCaseCarDetail | null
    )[] = [...compareCarDetailList ?? []];

    while (newArray.length < 4) {
      newArray.push(null);
    }
    return newArray;
  });
  const [
    selectedNavOption,
    setSelectedNavOption,
  ] = useState<NavOption>('overview');
  const [showStickyCarList, setStickyCarList] = useState(false);
  const stickyCarListEl = useRef<HTMLDivElement>(null);
  const overviewEl = useRef<HTMLDivElement>(null);
  const scrollContentEl = useRef<HTMLDivElement>(null);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [
    selectedCompareItemIndex,
    setSelectedCompareItemIndex,
  ] = useState<number | null>(null);

  useBodyScrollLock({ isLocked: isModalOpen });

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setIsScrollingUp(currentScrollY < lastScrollY);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isScrollingUp]);

  useEffect(() => {
    const listener = () => {
      if (
        !showStickyCarList
        && overviewEl.current
        && overviewEl.current.getBoundingClientRect().top <= 51
      ) {
        setStickyCarList(true);
      }

      if (
        showStickyCarList
        && overviewEl.current
        && overviewEl.current.getBoundingClientRect().top > 51
      ) {
        setStickyCarList(false);
      }
    };

    window.addEventListener('scroll', listener);
    return () => window.removeEventListener('scroll', listener);
  }, [showStickyCarList]);

  useEffect(() => {
    const listener = () => {
      if (stickyCarListEl.current && scrollContentEl.current) {
        stickyCarListEl.current.scrollLeft = scrollContentEl.current.scrollLeft;
      }
    };

    if (scrollContentEl.current) {
      scrollContentEl.current.addEventListener('scroll', listener);
    }
  }, []);

  useEffect(() => {
    if (showStickyCarList
      && stickyCarListEl.current
      && scrollContentEl.current) {
      stickyCarListEl.current.scrollLeft = scrollContentEl.current.scrollLeft;
    }
  }, [showStickyCarList]);

  const handleStickyCarListScroll = () => {
    if (scrollContentEl.current && stickyCarListEl.current) {
      scrollContentEl.current.scrollLeft = stickyCarListEl.current.scrollLeft;
    }
  };

  const handleNavClick = (type: NavOption) => () => {
    setSelectedNavOption(type);
  };

  const handleCompareModalSubmit = async (value: {
    make: string;
    id: number;
    submodelId: number;
  }) => {
    const formData = new FormData();

    formData.set('id', value.id.toString());

    const actionResult = await getCarDetailData(formData);
    const newCompareItems = [...compareList];

    if (actionResult.status === ACTION_STATUS_SUCCESS) {
      const camelCaseCarDetail = actionResult.data.data;

      newCompareItems[selectedCompareItemIndex ?? 0] = {
        ...camelCaseCarDetail,
        subModels: camelCaseCarDetail.subModels.filter(
          submodel => submodel.id === value.submodelId,
        ),
      };
      setCompareItems(newCompareItems);
    }

    const newcarItemsCookiesFormData = new FormData();

    newcarItemsCookiesFormData.set(
      'submodelIds',
      newCompareItems
        .filter(item => item !== null)
        .map(item => item?.subModels[0].id)
        .join(','),
    );

    newcarItemsCookiesFormData.set(
      'modelIds',
      newCompareItems
        .filter(item => item !== null)
        .map(item => item?.id)
        .join(','),
    );

    setCompareItems(newCompareItems);

    await setNewcarItemsCookies(newcarItemsCookiesFormData);
    setIsModalOpen(false);
    setSelectedCompareItemIndex(null);
  };

  const handleCompareItemRemove = (index: number) => async () => {
    const formData = new FormData();
    const newCompareItems = compareList.filter(
      (_, i) => i !== index,
    );

    while (newCompareItems.length < 4) {
      newCompareItems.push(null);
    }

    formData.set(
      'submodelIds',
      newCompareItems
        .filter(item => item !== null)
        .map(item => item?.subModels[0].id)
        .join(','),
    );

    formData.set(
      'modelIds',
      newCompareItems
        .filter(item => item !== null)
        .map(item => item?.id)
        .join(','),
    );

    setCompareItems(newCompareItems);

    await setNewcarItemsCookies(formData);
  };

  const handleAddClick = (index: number) => () => {
    setIsModalOpen(true);
    setSelectedCompareItemIndex(index);
  };

  const handleCompareModalClose = () => {
    setIsModalOpen(false);
    setSelectedCompareItemIndex(null);
  };

  const handleAlertModalSubmit = (
    alert: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    if (isCamelCaseCarAlert(alert) && myAlertList) {
      // remove
      if (!alert.adCarPriceChange && !alert.piCarPromo) {
        const newCarAlert = myAlertList.carAlerts
          .filter(carAlert => carAlert.carId !== alert.carId);

        if (newCarAlert) {
          setMyAlertList({ ...myAlertList, carAlerts: newCarAlert });
        }
      } else { // add
        setMyAlertList({
          ...myAlertList,
          carAlerts: [...myAlertList.carAlerts, alert],
        });
      }
    }
  };

  return (
    <main
      className={styles.containerPageRoot}>
      <SectionContainer
        title="Compare"
        titleHeading={TITLE_HEADING_DIV}>
        {null}
      </SectionContainer>
      <div
        ref={stickyCarListEl}
        className={cx(
          styles.containerStickyCarItems,
          showStickyCarList && styles.show,
          isScrollingUp && styles.scrollingUp,
        )}
        onScroll={handleStickyCarListScroll}>
        <div className={`${styles.containerScrollContentSticky}`}>
          {Array.from({ length: 4 }, (_, i) => {
            const list = compareList;
            return list[i] !== null
              ? (
                <div
                  key={list[i]?.subModels[0]?.id ?? `null-${i}`}
                  className={`${styles.containerStickyCarItemMobile} ${styles.containerScrollCell}`}>
                  <button
                    aria-label="Remove"
                    className={styles.iconClose}
                    type="button"
                    onClick={handleCompareItemRemove(i)}>
                    <IosClose
                      size={10} />
                  </button>
                  <span className={styles.lineClamp}>
                    {`${list[i]?.make} ${list[i]?.model} ${list[i]?.subModels[0]?.name}`}
                  </span>
                </div>
              )
              : (
                <div
                  key={`null-${i}`}
                  className={`${styles.containerScrollCell}`}>
                  <button
                    aria-label="Add"
                    type="button"
                    onClick={handleAddClick(i)}>
                    <svg
                      fill="none"
                      height="31"
                      viewBox="0 0 31 31"
                      width="31"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M15 0.945312C12.0333 0.945313 9.13319 1.82505 6.66645 3.47327C4.19972 5.12149 2.27713 7.46417 1.14181 10.2051C0.00649938 12.9459 -0.290551 15.9619 0.288227 18.8717C0.867006 21.7814 2.29562 24.4541 4.3934 26.5519C6.49119 28.6497 9.16393 30.0783 12.0736 30.6571C14.9834 31.2359 17.9994 30.9388 20.7403 29.8035C23.4811 28.6682 25.8238 26.7456 27.472 24.2789C29.1203 21.8121 30 18.912 30 15.9453C29.9986 11.9675 28.4178 8.15302 25.605 5.34029C22.7923 2.52756 18.9778 0.946744 15 0.945312ZM15 27.9453C12.6266 27.9453 10.3066 27.2415 8.33316 25.9229C6.35977 24.6044 4.8217 22.7302 3.91345 20.5375C3.0052 18.3448 2.76756 15.932 3.23058 13.6042C3.69361 11.2765 4.83649 9.13826 6.51472 7.46003C8.19295 5.7818 10.3311 4.63891 12.6589 4.17589C14.9867 3.71287 17.3995 3.95051 19.5922 4.85876C21.7849 5.76701 23.6591 7.30508 24.9776 9.27847C26.2962 11.2519 27 13.5719 27 15.9453C26.9957 19.1266 25.7301 22.1763 23.4805 24.4259C21.231 26.6754 18.1813 27.941 15 27.9453Z" // @suppress longLineCheck
                        fill="#C4C9CD" />
                      <path
                        d="M20.1426 14.6595H16.2858V10.8027C16.2858 10.6901 16.2636 10.5786 16.2205 10.4746C16.1774 10.3706 16.1143 10.2761 16.0347 10.1964C15.9551 10.1168 15.8605 10.0537 15.7565 10.0106C15.6525 9.96749 15.541 9.94531 15.4284 9.94531H14.5716C14.3442 9.94531 14.1261 10.0356 13.9653 10.1964C13.8045 10.3572 13.7142 10.5753 13.7142 10.8027V14.6601H9.8574C9.63011 14.6601 9.41212 14.7504 9.25134 14.911C9.09056 15.0717 9.00016 15.2896 9 15.5169V16.3743C9 16.6017 9.09033 16.8198 9.25113 16.9806C9.41192 17.1414 9.63 17.2317 9.8574 17.2317H13.7142V21.0891C13.7145 21.3163 13.805 21.5341 13.9657 21.6946C14.1265 21.8551 14.3444 21.9453 14.5716 21.9453H15.429C15.6564 21.9453 15.8745 21.855 16.0353 21.6942C16.1961 21.5334 16.2864 21.3153 16.2864 21.0879V17.2311H20.1438C20.371 17.2308 20.5888 17.1403 20.7493 16.9796C20.9098 16.8188 21 16.6009 21 16.3737V15.5169C21 15.2895 20.9097 15.0714 20.7489 14.9106C20.5881 14.7498 20.37 14.6595 20.1426 14.6595Z" // @suppress longLineCheck
                        fill="#C4C9CD" />
                    </svg>
                  </button>
                  <div>Add car to compare from car listing</div>
                </div>
              );
          })}
        </div>
      </div>
      <div
        className={cx(
          styles.containerStickyCarItemsNav,
          showStickyCarList && styles.show,
          isScrollingUp && styles.scrollingUp,
        )}>
        <Button
          className={cx(
            styles.btnNav,
            styles.isRounded,
            selectedNavOption !== 'overview' && styles.isNotBold,
          )}
          color={
            selectedNavOption === 'overview'
              ? BUTTON_COLOR_RED
              : BUTTON_COLOR_GRAY
          }
          variant={BUTTON_VARIANT_OUTLINED}
          onClick={handleNavClick('overview')}>
          Overview
        </Button>
        <Button
          className={cx(
            styles.btnNav,
            styles.isRounded,
            selectedNavOption !== 'specs' && styles.isNotBold,
          )}
          color={
            selectedNavOption === 'specs'
              ? BUTTON_COLOR_RED
              : BUTTON_COLOR_GRAY
          }
          variant={BUTTON_VARIANT_OUTLINED}
          onClick={handleNavClick('specs')}>
          Specs
        </Button>
        <Button
          className={cx(
            styles.btnNav,
            styles.isRounded,
            selectedNavOption !== 'features' && styles.isNotBold,
          )}
          color={
            selectedNavOption === 'features'
              ? BUTTON_COLOR_RED
              : BUTTON_COLOR_GRAY
          }
          variant={BUTTON_VARIANT_OUTLINED}
          onClick={handleNavClick('features')}>
          Features
        </Button>
      </div>
      {/* End of sticky car items on mobile */}
      <div
        ref={scrollContentEl}
        className={styles.containerScrollWrapper}>
        <div className={`${styles.containerScrollContent}`}>
          {Array.from({ length: 4 }, (_, i) => {
            const list = compareList;

            let carAlerts: CamelCaseCarAlertList = [];
            let adAlerts: CamelCaseAdAlertList = [];

            if (myAlertList) {
              ({ adAlerts, carAlerts } = myAlertList);
            }

            const matchingCarAlerts = carAlerts.filter(
              alert => alert.carId === list[i]?.id,
            );
            const matchingAdAlerts = adAlerts.filter(
              alert => alert.make === list[i]?.make,
            );

            const alertList: AlertList = {
              adAlert: matchingAdAlerts.length
                ? matchingAdAlerts[0]
                : {
                  make: list[i]?.make ?? '',
                  adPromo: false,
                  adCarLaunches: false,
                },
              carAlert: matchingCarAlerts.length
                ? matchingCarAlerts[0]
                : {
                  carId: list[i]?.id ?? 0,
                  adCarPriceChange: false,
                  piCarPromo: false,
                },
            };

            return list[i] !== null
              ? (
                <CarItem
                  key={list[i]?.subModels[0]?.id}
                  alertList={alertList}
                  index={i}
                  item={list[i]}
                  onAlertModalSubmit={handleAlertModalSubmit}
                  onChange={handleAddClick(i)}
                  onRemove={handleCompareItemRemove(i)} />
              )
              : (
                <div
                  key={`null-${i}`}
                  className={`${styles.containerScrollCell} ${styles.containerCarItemMobile}`}>
                  <div className={styles.containerAddCar}>
                    <button
                      aria-label="Add"
                      type="button"
                      onClick={handleAddClick(i)}>
                      <svg
                        fill="none"
                        height="31"
                        viewBox="0 0 31 31"
                        width="31"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M15 0.945312C12.0333 0.945313 9.13319 1.82505 6.66645 3.47327C4.19972 5.12149 2.27713 7.46417 1.14181 10.2051C0.00649938 12.9459 -0.290551 15.9619 0.288227 18.8717C0.867006 21.7814 2.29562 24.4541 4.3934 26.5519C6.49119 28.6497 9.16393 30.0783 12.0736 30.6571C14.9834 31.2359 17.9994 30.9388 20.7403 29.8035C23.4811 28.6682 25.8238 26.7456 27.472 24.2789C29.1203 21.8121 30 18.912 30 15.9453C29.9986 11.9675 28.4178 8.15302 25.605 5.34029C22.7923 2.52756 18.9778 0.946744 15 0.945312ZM15 27.9453C12.6266 27.9453 10.3066 27.2415 8.33316 25.9229C6.35977 24.6044 4.8217 22.7302 3.91345 20.5375C3.0052 18.3448 2.76756 15.932 3.23058 13.6042C3.69361 11.2765 4.83649 9.13826 6.51472 7.46003C8.19295 5.7818 10.3311 4.63891 12.6589 4.17589C14.9867 3.71287 17.3995 3.95051 19.5922 4.85876C21.7849 5.76701 23.6591 7.30508 24.9776 9.27847C26.2962 11.2519 27 13.5719 27 15.9453C26.9957 19.1266 25.7301 22.1763 23.4805 24.4259C21.231 26.6754 18.1813 27.941 15 27.9453Z" // @suppress longLineCheck
                          fill="#C4C9CD" />
                        <path
                          d="M20.1426 14.6595H16.2858V10.8027C16.2858 10.6901 16.2636 10.5786 16.2205 10.4746C16.1774 10.3706 16.1143 10.2761 16.0347 10.1964C15.9551 10.1168 15.8605 10.0537 15.7565 10.0106C15.6525 9.96749 15.541 9.94531 15.4284 9.94531H14.5716C14.3442 9.94531 14.1261 10.0356 13.9653 10.1964C13.8045 10.3572 13.7142 10.5753 13.7142 10.8027V14.6601H9.8574C9.63011 14.6601 9.41212 14.7504 9.25134 14.911C9.09056 15.0717 9.00016 15.2896 9 15.5169V16.3743C9 16.6017 9.09033 16.8198 9.25113 16.9806C9.41192 17.1414 9.63 17.2317 9.8574 17.2317H13.7142V21.0891C13.7145 21.3163 13.805 21.5341 13.9657 21.6946C14.1265 21.8551 14.3444 21.9453 14.5716 21.9453H15.429C15.6564 21.9453 15.8745 21.855 16.0353 21.6942C16.1961 21.5334 16.2864 21.3153 16.2864 21.0879V17.2311H20.1438C20.371 17.2308 20.5888 17.1403 20.7493 16.9796C20.9098 16.8188 21 16.6009 21 16.3737V15.5169C21 15.2895 20.9097 15.0714 20.7489 14.9106C20.5881 14.7498 20.37 14.6595 20.1426 14.6595Z" // @suppress longLineCheck
                          fill="#C4C9CD" />
                      </svg>
                    </button>
                    <div>Add car to compare from car listing</div>
                  </div>
                </div>
              );
          })}
        </div>
        <div
          ref={overviewEl}
          className={`${styles.containerStickyRow} ${styles.overview}`}>
          <Button
            className={cx(
              styles.btnNav,
              styles.isRounded,
              selectedNavOption !== 'overview' && styles.isNotBold,
            )}
            color={
              selectedNavOption === 'overview'
                ? BUTTON_COLOR_RED
                : BUTTON_COLOR_GRAY
            }
            variant={BUTTON_VARIANT_OUTLINED}
            onClick={handleNavClick('overview')}>
            Overview
          </Button>
          <Button
            className={cx(
              styles.btnNav,
              styles.isRounded,
              selectedNavOption !== 'specs' && styles.isNotBold,
            )}
            color={
              selectedNavOption === 'specs'
                ? BUTTON_COLOR_RED
                : BUTTON_COLOR_GRAY
            }
            variant={BUTTON_VARIANT_OUTLINED}
            onClick={handleNavClick('specs')}>
            Specs
          </Button>
          <Button
            className={cx(
              styles.btnNav,
              styles.isRounded,
              selectedNavOption !== 'features' && styles.isNotBold,
            )}
            color={
              selectedNavOption === 'features'
                ? BUTTON_COLOR_RED
                : BUTTON_COLOR_GRAY
            }
            variant={BUTTON_VARIANT_OUTLINED}
            onClick={handleNavClick('features')}>
            Features
          </Button>
        </div>
        {selectedNavOption === 'overview'
        && (
          <OverviewTable
            compareList={compareList}
            promotonList={promotions} />
        )}
        {selectedNavOption === 'specs'
        && <SpecsTable compareList={compareList} />}
        {selectedNavOption === 'features'
        && <FeaturesTable compareList={compareList} />}
      </div>
      <CompareModal
        brandList={brandList}
        isOpen={isModalOpen}
        onClose={handleCompareModalClose}
        onSubmit={handleCompareModalSubmit} />
    </main>
  );
};
